<template>
  <div id="app">
    <div v-if="isStandaloneRoute" class="standalone-component-container">
      <router-view />
    </div>
    <template v-else>
      <nav-bar />
      <bread-crumb />
      <div id="main-content-and-footer">
        <main-content />
        <app-footer />
      </div>
      <patch-notes-editor ref="modalRef" modal-id="patch-notes-view-only" />
    </template>
    <PVToast position="bottom-right" />
  </div>
</template>

<script>
  import NavBar from './components/NavBar/NavBar.vue';
  import AppFooter from './components/AppFooter/AppFooter.vue';
  import MainContent from './components/MainContent/MainContent.vue';
  import PatchNotesEditor from '@/components/PatchNotesEditor/PatchNotesEditor.vue';
  import BreadCrumb from '@/components/BreadCrumb/BreadCrumb.vue';
  import VueMarkdownEditor from '@kangc/v-md-editor';
  import 'vue-multiselect/dist/vue-multiselect.min.css';
  import { TARGET_URL_LOCAL_STORAGE_KEY } from './interfaces/constants';
  import enUS from '@kangc/v-md-editor/lib/lang/en-US';
  import deDE from '@kangc/v-md-editor/lib/lang/de-DE';
  import { POPUP_LOGIN_REDIRECTION_ROUTE, Route } from './routes';

  export default {
    name: 'DlpRoot',
    components: {
      NavBar,
      BreadCrumb,
      AppFooter,
      MainContent,
      PatchNotesEditor,
    },

    computed: {
      isStandaloneRoute() {
        return [
          Route.KKS_VGBM_FORM,
          Route.IFRAME_LOGIN_VIEW,
          Route.OAUTH_CLOSE_VIEW,
        ].includes(this.$route.name);
      },
    },

    watch: {
      '$store.state.language': {
        handler() {
          this.setMarkdownEditorLanguage();
        },
        immediate: true,
      },
      '$store.state.isCurrentPatchNotePending': {
        handler() {
          const isPending = this.$store.state.isCurrentPatchNotePending;
          if (isPending) {
            this.$nextTick(() => {
              this.showPendingPatchNote();
            });

            this.$store.commit('setIsCurrentPatchNotePending', false);
          }
        },
        deep: true,
        immediate: true,
      },
    },

    created() {
      if (
        !['http://localhost:8081', 'https://localhost:8080'].includes(
          window.location.origin,
        )
      ) {
        window.addEventListener('beforeunload', this.onBeforeUnload);
      }
    },

    mounted() {
      this.setAppLanguage();
      this.$store.dispatch('fetchCurrentUserInfo', this.$router);

      if (!window.location.href.includes(POPUP_LOGIN_REDIRECTION_ROUTE)) {
        const targetUrl = localStorage.getItem(TARGET_URL_LOCAL_STORAGE_KEY);
        if (targetUrl) {
          localStorage.removeItem(TARGET_URL_LOCAL_STORAGE_KEY);
          this.$router.replace(targetUrl.split('#')[1]);
        }
      }
    },

    methods: {
      setAppLanguage() {
        this.$store.commit(
          'setLang',
          window.localStorage.getItem('lang') || 'de',
        );
        this.$i18n.locale = this.$store.state.language;
        this.setMarkdownEditorLanguage();
      },

      setMarkdownEditorLanguage() {
        switch (this.$store.state.language) {
          case 'de':
            VueMarkdownEditor.lang.use('de-DE', deDE);
            break;
          default:
            VueMarkdownEditor.lang.use('en-US', enUS);
        }
      },

      onBeforeUnload(event) {
        event.preventDefault();
        event.returnValue = this.t('app.logOutReminder');
        return this.t('app.logOutReminder');
      },

      showPendingPatchNote() {
        this.$refs.modalRef?.show();
      },
    },
  };
</script>

<style lang="scss">
  body {
    position: relative;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 100vh;
  }

  #app {
    position: absolute;
    top: 0;
    left: 0;
    min-width: 100%;
    min-height: 100vh;
    background-color: $app-background-color;
    color: $primary-font-color;

    #main-content-and-footer {
      height: calc(100vh - $nav-bar-height - $bread-crumb-height);
      overflow-y: auto;
    }

    .standalone-component-container {
      height: 100vh;
      padding: 1rem 3rem;
      background-color: white;
    }
  }

  .break-all-words {
    word-break: break-all !important;
  }
</style>
